import { PencilIcon } from "@heroicons/react/outline";
import { useState } from "react";
import { ICellRendererParams } from "ag-grid-community";
import moment from "moment";
import { useParams } from "react-router-dom";

import { ApiKeyModalContent } from "./ApiKeyModal/ApiKeyModalContent";
import { sortByCreatedDate } from "./utils";

import { DataTable } from "@/components/ui/ag-grid-table";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { TableColumn } from "@/components/ui/TableV2";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useDataTableDeleteConfirm } from "@/hooks/useDataTableDeleteConfirm";
import { useDeletePromptLayerApiKey, useUpdateApiKeyName } from "@/queries";
import { PromptLayerApiKey } from "@/types/promptLayerApiKeys";
import { formatTimestamp } from "@/utils/timestamp";

export const PromptLayerApiKeyTable = () => {
  const onRequestDeleteConfirm = useDataTableDeleteConfirm();
  const userContext = useUser();
  const authContext = useAuth();
  const { workspaceId } = useParams();
  const apiKeys = userContext.promptLayerWorkspaceApiKeys;

  const { mutateAsync: deletePromptLayerApiKey } = useDeletePromptLayerApiKey(
    authContext!.userToken!,
    Number(workspaceId),
  );
  const { mutateAsync: updateApiKeyName } = useUpdateApiKeyName(authContext!.userToken!);

  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameKeyId, setRenameKeyId] = useState<number | null>(null);
  const [renameName, setRenameName] = useState("");

  const handleRename = (item?: PromptLayerApiKey) => {
    if (!item) {
      return;
    }
    setRenameKeyId(item.id);
    setRenameName(item.name || "");
    setRenameDialogOpen(true);
  };

  const handleConfirmRename = async () => {
    if (renameKeyId === null) {
      return;
    }
    await updateApiKeyName({ id: renameKeyId, name: renameName.trim() || undefined });
    setRenameDialogOpen(false);
  };

  const columns: TableColumn<(typeof apiKeys)[0]>[] = [
    {
      id: 0,
      name: "Name",
      header: "Name",
      field: "name",
      // eslint-disable-next-line react/no-unstable-nested-components
      cellRenderer: (params: ICellRendererParams<PromptLayerApiKey>) => (
        <span className="text-gray-700">{params.data?.name || "—"}</span>
      ),
    },
    {
      id: 1,
      name: "API Key",
      header: "API Key",
      sortable: false,
      cellRenderer: (params: ICellRendererParams<PromptLayerApiKey>) => {
        const suffix = params.data?.key_suffix;
        return suffix ? `••••••••••••••••••••${suffix}` : "••••••••••••••••••••••••";
      },
    },
    {
      id: 2,
      name: "User",
      header: "User",
      field: "email",
    },
    {
      id: 3,
      name: "Created At",
      header: "Created At",
      field: "created_at",
      comparator: (valueA, valueB) => {
        if (!valueA || !valueB) {
          return 0;
        }
        return moment(valueA).diff(moment(valueB));
      },
      // eslint-disable-next-line react/no-unstable-nested-components
      cellRenderer: (params: ICellRendererParams<PromptLayerApiKey>) => (
        <span title={formatTimestamp(params.data?.created_at || "")}>
          {formatTimestamp(params.data?.created_at || "")}
        </span>
      ),
    },
    {
      id: 4,
      name: "Last Active",
      header: "Last Active",
      field: "last_active",
      comparator: (valueA, valueB) => {
        if (!valueA || !valueB) {
          return 0;
        }
        return moment(valueA).diff(moment(valueB));
      },
      // eslint-disable-next-line react/no-unstable-nested-components
      cellRenderer: (params: ICellRendererParams<PromptLayerApiKey>) => (
        <span title={formatTimestamp(params.data?.last_active || "")}>
          {formatTimestamp(params.data?.last_active || "")}
        </span>
      ),
    },
  ];

  return (
    <div className="flex h-full flex-col">
      <DataTable
        data={sortByCreatedDate(apiKeys)}
        action={<ApiKeyModalContent />}
        columns={columns}
        rowsDeletable
        onRequestDeleteConfirm={onRequestDeleteConfirm}
        onDelete={item => deletePromptLayerApiKey(item?.id ?? 0)}
        emptyStateMessage="No PromptLayer API keys found."
        objectName="API key"
        actions={[
          {
            id: "rename",
            label: "Rename",
            icon: PencilIcon,
            onClick: handleRename,
          },
        ]}
      />
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent className="max-w-[400px]">
          <div className="flex flex-col gap-4 p-1">
            <h3 className="text-lg font-semibold leading-6 text-gray-900">Rename API Key</h3>
            <Input
              placeholder="e.g. Production, Staging, CI/CD"
              value={renameName}
              onChange={e => setRenameName(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  handleConfirmRename();
                }
              }}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleConfirmRename}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
